import i18next from "i18next";
import { BookingLanguage } from "@/types/booking";
import { BOOKING_NS, languageMap } from "@/i18n";

// Import JSON only for TypeScript types (not used at runtime for translations)
import type enTranslations from "./en.json";

// Type for flat translation keys
export type TranslationKey = keyof typeof enTranslations;

// Type for the translations object
export type Translations = typeof enTranslations;

/**
 * Convert BookingLanguage to i18next language code
 */
const toI18nLang = (language: BookingLanguage): string => {
  return languageMap[language] || "en";
};

/**
 * Get a translation by key using i18next
 * @param language - The language code ('en-US' or 'de-DE')
 * @param key - The flat translation key (e.g., 'bookingForm.firstName')
 * @returns The translated string
 */
export const t = (language: BookingLanguage, key: string): string => {
  const i18nLang = toI18nLang(language);
  return i18next.t(key, { lng: i18nLang, ns: BOOKING_NS }) || key;
};

/**
 * Get a translation with fallback to English using i18next
 * @param language - The language code
 * @param key - The translation key
 * @param fallback - Optional fallback value if key not found
 */
export const getTranslation = (
  language: BookingLanguage,
  key: string,
  fallback?: string,
): string => {
  const i18nLang = toI18nLang(language);
  const result = i18next.t(key, {
    lng: i18nLang,
    ns: BOOKING_NS,
    defaultValue: "",
  });
  if (result) return result;
  // Try English fallback
  const enResult = i18next.t(key, {
    lng: "en",
    ns: BOOKING_NS,
    defaultValue: "",
  });
  if (enResult) return enResult;
  return fallback || key;
};

/**
 * Resolve a translation key to its translated value.
 * Looks up the value in the locale files, returns the translation if found, otherwise returns the key.
 *
 * @param language - The language code
 * @param value - The translation key
 * @returns The translated string or the key if not found
 */
export const resolveProductKey = (
  language: BookingLanguage,
  value: string | null | undefined,
): string => {
  if (!value) return "";

  const i18nLang = toI18nLang(language);
  return i18next.t(value, {
    lng: i18nLang,
    ns: BOOKING_NS,
    defaultValue: value,
  });
};

/**
 * Resolve a clinic's display name for the active language. Clinic names are
 * keyed in the locale files by the clinic's `human_readable_id` (AHD-7940); falls back to
 * the raw `clinic.name` when there's no key/translation, so a new clinic never
 * renders a bare slug.
 */
export const resolveClinicName = (
  language: BookingLanguage,
  clinic: { human_readable_id?: string | null; name: string },
): string => {
  if (!clinic.human_readable_id) return clinic.name;
  const i18nLang = toI18nLang(language);
  return i18next.t(clinic.human_readable_id, {
    lng: i18nLang,
    ns: BOOKING_NS,
    defaultValue: clinic.name,
  });
};
